<template>
    <div class="p-2">
        <grid
            ref="grid"
            class="statements-grid"
            :columnDefs="columnDefs"
            :rowData="ownerStatements"
            :getRowClass="getRowClass"
            :customGridHeight="ownerStatementsGridHeight"
            :isAutoHeight="ownerStatementsGridHeight == ''"
            :loading="!isChildrenFetched"
            :suppressRowClickSelection="true"
            :master-detail="true"
            :detailCellRendererParams="{
                updateOwnerStatement: updateOwnerStatement,
                columnSettings: params.columnSettings,
            }"
            :detailCellRenderer="detailCellRenderer"
            :onCellClicked="onCellClicked"
            rowSelection="multiple"
            :isSearch="false"
            :isGroupOpenByDefault="() => true"
            @selection-changed="handleSelectionChange"
            @row-group-opened="handleRowGroupOpened"
            grid-theme="ag-theme-alpine-owner-statement"
        >
            <bulk-actions
                v-if="hasSelectedRows"
                :selected-count="currentSelectedRows.length"
                :bulk-action-defs="bulkActionDefs"
            />
        </grid>
        <validation-observer ref="ref_actions" v-slot="{ meta, setValues }">
            <modal
                :id="deleteModalId"
                :title="`Delete Owner Statement${statementPostfix}`"
                ok-title="Delete"
                :ok-disabled="loading"
                @ok.prevent="bulkDeleteOwnerStatements"
            >
                <busy-modal :show="loading"></busy-modal>
                Are you sure you want to delete {{ statementPrefix }} Owner Statement{{ statementPostfix }}?
            </modal>
            <owner-statement-publish-unpublish-modal
                :modal-id="publishModalId"
                action="publish"
                :is-single-owner-selected="isSingleOwnerSelected()"
                :owners-data="getOwnersData()"
                :loading="loading"
                :is-bulk-action="true"
                :statement-prefix="statementPrefix"
                :statement-postfix="statementPostfix"
                @ok="bulkPublishOwnerStatements"
            />
            <owner-statement-publish-unpublish-modal
                :modal-id="unpublishModalId"
                action="unpublish"
                :loading="loading"
                :is-bulk-action="true"
                :statement-prefix="statementPrefix"
                :statement-postfix="statementPostfix"
                @ok="bulkUnpublishOwnerStatements"
            />
            <modal
                :id="regenerateModalId"
                :title="`Regenerate Owner Statement${statementPostfix}`"
                ok-title="Regenerate"
                :ok-disabled="loading"
                @ok.prevent="bulkRegenerateOwnerStatements"
            >
                <busy-modal :show="loading"></busy-modal>
                Are you sure you want to regenerate {{ statementPrefix }} Owner Statement{{ statementPostfix }}?
            </modal>
            <modal
                :id="commentsToOwnerContentModalId"
                title="Comments to Owner"
                size="lg"
                scrollable
                @ok="submitComments"
                :ok-disabled="isCommentsPublished"
                ok-title="Save"
                cancel-title="Cancel"
            >
                <validation-provider
                    name="newComments"
                    v-slot="{ errors, handleChange }"
                    rules="maxMessageLenOrEqual:450,Comments"
                    class="d-flex flex-column"
                >
                    <form-textarea
                        v-model="newComments"
                        rows="7"
                        :disabled="isCommentsPublished"
                        :description="commentsDescription"
                        :left-icon="['fas', 'note-sticky']"
                        :error="errors[0]"
                        @input="handleChange"
                    ></form-textarea>
                </validation-provider>
            </modal>
        </validation-observer>
    </div>
</template>
<script>
    import API from '../../api';
    import grid from '../generic/grid/grid.vue';
    import Modal from '../generic/modal/modal.vue';
    import BusyModal from '../generic/busy-modal/busy-modal.vue';
    import FormSwitch from '../generic/form-switch/form-switch.vue';
    import FormTextarea from '../generic/form-textarea/form-textarea.vue';
    import BulkActions from '../generic/bulk-actions/bulk-actions.vue';
    import ActionsCellRenderer from './actions-cell-renderer.vue';
    import AdditionalTablesDetailCellRenderer from './additional-tables-detail-cell-renderer.vue';
    import OwnerStatementPublishUnpublishModal from './owner-statement-publish-unpublish-modal.vue';
    import { OWNER_STATEMENT_ENDPOINT } from '../../endpoints';
    import { formatResponseError } from '../../utils/api';

    export default {
        components: {
            grid,
            Modal,
            BusyModal,
            FormSwitch,
            FormTextarea,
            BulkActions,
            actionsCellRenderer: ActionsCellRenderer,
            AdditionalTablesDetailCellRenderer,
            OwnerStatementPublishUnpublishModal,
        },
        data() {
            return {
                loading: false,
                newComments: '',
                generationData: {},
                currentSelectedRows: [],
                commentsToOwnerContent: '',
                currentEditingRowId: null,
                isCommentsPublished: false,
                detailCellRenderer: 'AdditionalTablesDetailCellRenderer',
                commentsToOwnerContentModalId: 'comments-to-owner-content-model-id',
            };
        },
        mounted() {
            const grid = this.$refs.grid;
            this.gridApi = grid.gridOptions.api;
        },
        created() {
            this.generationData = this.params.data || {};
            this.deleteModalId = `modal-delete-id-${this.generationData.id}`;
            this.publishModalId = `modal-publish-id-${this.generationData.id}`;
            this.unpublishModalId = `modal-unpublish-id-${this.generationData.id}`;
            this.regenerateModalId = `modal-regenerate-id-${this.generationData.id}`;
            this.isChildrenFetched = this.generationData.is_children_fetched;
        },
        computed: {
            bulkActionDefs() {
                return [
                    {
                        label: 'Publish',
                        group: '',
                        onClick: this.handlePublishAction,
                    },
                    {
                        label: 'Unpublish',
                        group: '',
                        onClick: this.handleUnpublishAction,
                    },
                    {
                        label: 'Regenerate',
                        group: '',
                        onClick: this.handleRegenerateAction,
                        disabled: this.hasPublishedSelectedRow,
                    },
                    {
                        label: 'Delete',
                        group: '',
                        onClick: this.handleDeleteAction,
                        disabled: this.hasPublishedSelectedRow,
                    },
                ];
            },
            ownerStatements() {
                return this.params.data.children;
            },
            ownerStatementsGridHeight() {
                return this.ownerStatements.length === 0 ? '200' : '';
            },
            columnDefs() {
                const dimIfNoActivity = (params) => {
                    const hasNoActivity =
                        (!params.data?.reservation_count || params.data.reservation_count === 0) &&
                        (!params.data?.work_order_count || params.data.work_order_count === 0) &&
                        (!params.data?.rental_expense_count || params.data.rental_expense_count === 0);
                    return hasNoActivity ? { opacity: 0.5 } : null;
                };

                return [
                    {
                        field: 'id',
                        cellRenderer: 'agGroupCellRenderer',
                        headerName: '',
                        cellRendererParams: {
                            innerRenderer: () => {
                                return '';
                            },
                        },
                        minWidth: 80,
                        maxWidth: 80,
                        checkboxSelection: true,
                        headerCheckboxSelection: true,
                    },
                    {
                        field: 'generated',
                        headerName: 'Generated',
                        type: 'dateTimeColumn',
                        sortable: true,
                        minWidth: 180,
                        maxWidth: 200,
                        cellStyle: dimIfNoActivity,
                    },
                    {
                        field: 'owner_name',
                        headerName: 'Owner',
                        sortable: true,
                        cellStyle: dimIfNoActivity,
                    },
                    {
                        field: 'property_name',
                        headerName: 'Property',
                        sortable: true,
                        minWidth: 320,
                        cellStyle: dimIfNoActivity,
                    },
                    {
                        field: 'reservation_count',
                        headerName: 'Reservations',
                        sortable: true,
                        minWidth: 110,
                        maxWidth: 110,
                        valueGetter: (params) => {
                            return params.data?.reservation_count || 0;
                        },
                        cellStyle: dimIfNoActivity,
                    },
                    {
                        field: 'work_order_count',
                        headerName: 'Work Orders',
                        sortable: true,
                        minWidth: 105,
                        maxWidth: 105,
                        valueGetter: (params) => {
                            return params.data?.work_order_count || 0;
                        },
                        cellStyle: dimIfNoActivity,
                    },
                    {
                        field: 'rental_expense_count',
                        headerName: 'Expenses',
                        sortable: true,
                        minWidth: 90,
                        maxWidth: 90,
                        valueGetter: (params) => {
                            return params.data?.rental_expense_count || 0;
                        },
                        cellStyle: dimIfNoActivity,
                    },
                    {
                        type: 'badgeColumn',
                        field: 'comments',
                        headerName: 'Comments to Owner',
                        sortable: false,
                        minWidth: 160,
                        maxWidth: 160,
                        autoWidthExclude: true,
                        valueGetter: (params) => {
                            return !params.data.comments ? 'Add Comment' : params.data.comments;
                        },
                        cellRendererParams: {
                            badgeSize: 0.84,
                            isClickable: true,
                            badgeVariant: ({ data }) => {
                                return !data.comments ? 'primary' : '';
                            },
                        },
                    },
                    {
                        type: 'badgeColumn',
                        field: 'status',
                        headerName: 'Status',
                        headerClass: 'statement-status-column',
                        sortable: false,
                        minWidth: 110,
                        maxWidth: 110,
                        autoWidthExclude: true,
                        cellRendererParams: {
                            badgeSize: 0.84,
                            badgeVariant: ({ data }) => {
                                if (data.status === 'Published') return 'success';
                                else if (data.status === 'In Review') return 'light';
                                else return '';
                            },
                        },
                    },
                    {
                        headerName: '',
                        colId: 'actionsColumn',
                        minWidth: 214,
                        maxWidth: 214,
                        resizable: false,
                        cellStyle: { textAlign: 'center' },
                        cellRenderer: 'actionsCellRenderer',
                        cellRendererParams: {
                            onDeleteSuccess: this.onDeleteSuccess,
                            onPublishSuccess: this.onPublishSuccess,
                            onUnpublishSuccess: this.onUnpublishSuccess,
                            onRegenerateSuccess: this.onRegenerateSuccess,
                        },
                    },
                ];
            },
            commentsDescription() {
                return this.isCommentsPublished
                    ? `To ${
                          this.newComments ? 'update your' : 'add a'
                      } comment, temporarily unpublish the statement (you can republish it once your comment is saved).`
                    : '';
            },

            statementPrefix() {
                return this.currentSelectedRows.length > 1 ? 'these' : 'this';
            },
            statementPostfix() {
                return this.currentSelectedRows.length > 1 ? 's' : '';
            },
            hasSelectedRows() {
                return this.currentSelectedRows.length > 0;
            },
            hasPublishedSelectedRow() {
                return !!this.currentSelectedRows.find((row) => row.status === 'Published');
            },
        },
        methods: {
            checkAndRemoveGenerationIfEmpty() {
                const newStatementsCount = this.params.data.children.length;
                if (newStatementsCount === 0 && this.params.deleteGeneration) {
                    this.params.deleteGeneration(this.generationData.id);
                }
            },
            onCellClicked(event) {
                if (event.column.colId === 'comments') {
                    this.isCommentsPublished = event.data.status === 'Published';
                    this.openCommentsModal(event.data.comments, event.node.id);
                } else if (event.column.colId !== 'actionsColumn') {
                    this.onRowClicked(event);
                }
            },
            onRowClicked(event) {
                if (event.node.master) {
                    event.node.setExpanded(!event.node.expanded);
                }
            },
            getOwnerStatementId() {
                if (!this.currentSelectedRows || this.currentSelectedRows.length === 0) return null;
                return this.currentSelectedRows[0].id;
            },
            isSingleOwnerSelected() {
                if (!this.currentSelectedRows || this.currentSelectedRows.length === 0) return false;
                const firstId = this.currentSelectedRows[0].owner_id;
                return this.currentSelectedRows.every((row) => row.owner_id === firstId);
            },
            getOwnersData() {
                if (!this.currentSelectedRows || this.currentSelectedRows.length === 0) return [];
                const ownerMap = new Map();
                this.currentSelectedRows.forEach((row) => {
                    if (!ownerMap.has(row.owner_id)) {
                        ownerMap.set(row.owner_id, {
                            name: row.owner_name,
                            email: row.owner_email,
                            statementId: row.id,
                        });
                    }
                });
                return Array.from(ownerMap).map(([id, data]) => ({ statementId: data.statementId }));
            },
            handlePublishAction() {
                this.$bvModal.show(this.publishModalId);
            },
            handleUnpublishAction() {
                this.$bvModal.show(this.unpublishModalId);
            },
            handleRegenerateAction() {
                this.$bvModal.show(this.regenerateModalId);
            },
            handleDeleteAction() {
                this.$bvModal.show(this.deleteModalId);
            },
            async bulkPublishOwnerStatements(params) {
                this.loading = true;
                try {
                    const ownerStatementIds = this.currentSelectedRows
                        .filter((row) => row.status === 'In Review')
                        .map((row) => row.id);
                    await API.post(OWNER_STATEMENT_ENDPOINT() + 'bulk-publish/', {
                        owner_statement_ids: ownerStatementIds,
                        save_to_owner_account: params.saveToOwnerAccount,
                        send_email_to_owner: params.sendEmailToOwner,
                        email_data: params.emailData,
                    });
                    this.successToast({ text: 'All selected owner statements published successfully.' });
                    this.updateGridRowDataStatements(ownerStatementIds, 'Published');
                    this.unselectAllRows();
                    this.params.updatePublishedCount(this.generationData.id, ownerStatementIds.length);
                } catch (error) {
                    const { generic } = formatResponseError(error);
                    this.errorToast({ text: generic || `Failed to publish owner statements: ${error}` });
                } finally {
                    this.loading = false;
                    this.$bvModal.hide(this.publishModalId);
                }
            },
            async bulkUnpublishOwnerStatements() {
                this.loading = true;
                try {
                    const ownerStatementIds = this.currentSelectedRows
                        .filter((row) => row.status === 'Published')
                        .map((row) => row.id);
                    await API.post(OWNER_STATEMENT_ENDPOINT() + 'bulk-unpublish/', {
                        owner_statement_ids: ownerStatementIds,
                    });
                    this.successToast({ text: 'All selected owner statements unpublished successfully.' });
                    this.updateGridRowDataStatements(ownerStatementIds, 'In Review');
                    this.unselectAllRows();
                    this.params.updatePublishedCount(this.generationData.id, -ownerStatementIds.length);
                } catch (error) {
                    const { generic } = formatResponseError(error);
                    this.errorToast({ text: generic || `Failed to unpublish owner statements: ${error}` });
                } finally {
                    this.loading = false;
                    this.$bvModal.hide(this.unpublishModalId);
                }
            },
            async bulkDeleteOwnerStatements() {
                this.loading = true;
                try {
                    const ownerStatementIds = this.currentSelectedRows.map((row) => row.id);
                    await API.post(OWNER_STATEMENT_ENDPOINT() + 'bulk-delete/', {
                        owner_statement_ids: ownerStatementIds,
                    });
                    this.successToast({ text: 'All selected owner statements deleted successfully.' });
                    this.deleteStatementsFromGridRowData(ownerStatementIds);
                    this.unselectAllRows();
                } catch (error) {
                    const { generic } = formatResponseError(error);
                    this.errorToast({ text: generic || `Failed to delete owner statements: ${error}` });
                } finally {
                    this.loading = false;
                    this.$bvModal.hide(this.deleteModalId);
                }
            },
            async bulkRegenerateOwnerStatements() {
                this.loading = true;
                try {
                    const ownerStatementIds = this.currentSelectedRows.map((row) => row.id);
                    await API.post(OWNER_STATEMENT_ENDPOINT() + 'bulk-regenerate/', {
                        owner_statement_ids: ownerStatementIds,
                    });
                    this.params.handleOwnerStatementsRegenerated(this.generationData.id);
                    this.unselectAllRows();
                } catch (error) {
                    const { generic } = formatResponseError(error);
                    this.errorToast({ text: generic || `Failed to regenerate owner statements: ${error}` });
                } finally {
                    this.loading = false;
                    this.$bvModal.hide(this.regenerateModalId);
                }
            },
            onDeleteSuccess(id) {
                this.successToast({ text: `Owner Statement successfully deleted.` });
                this.deleteStatementFromGridRowData(id);
            },
            onRegenerateSuccess(data) {
                this.successToast({ text: `Owner Statement successfully regenerated.` });
                this.updateGridRowDataStatement(data);
            },
            onPublishSuccess(data) {
                this.successToast({ text: `Owner Statement successfully published.` });
                this.params.updatePublishedCount(data.generation_id, 1);
                this.updateGridRowDataStatement(data);
            },
            onUnpublishSuccess(data) {
                this.successToast({ text: `Owner Statement successfully unpublished.` });
                this.params.updatePublishedCount(data.generation_id, -1);
                this.updateGridRowDataStatement(data);
            },
            updateOwnerStatement(data) {
                this.updateGridRowDataStatement(data);
            },
            unselectAllRows() {
                if (this.gridApi) {
                    this.gridApi.deselectAll();
                }
                this.currentSelectedRows = [];
            },
            async handleRowGroupOpened(event) {
                if (event.node.expanded && !('reservations' in event.data)) {
                    await this.fetchOwnerStatement(event.data.id);
                }
            },
            updateGridRowDataStatement(ownerStatement) {
                this.gridApi.forEachNode((node) => {
                    if (ownerStatement && ownerStatement.id === node.data.id) {
                        node.data = ownerStatement;
                        this.gridApi.applyTransaction({ update: [node.data] });
                        const index = this.params.data.children.findIndex((child) => child.id === ownerStatement.id);
                        if (index !== -1) this.$set(this.params.data.children, index, ownerStatement);
                    }
                });
            },
            updateGridRowDataStatements(statementIds, status) {
                this.gridApi.forEachNode((node) => {
                    const statement = statementIds.find((id) => id === node.data.id);
                    if (statement) {
                        node.setDataValue('status', status);
                        const index = this.params.data.children.findIndex((child) => child.id === node.data.id);
                        if (index !== -1) this.$set(this.params.data.children[index], 'status', status);
                    }
                });
            },
            deleteStatementFromGridRowData(id) {
                this.gridApi.forEachNode((node) => {
                    if (id === node.data.id) {
                        const newStatementsCount = this.generationData.statements_count - 1;
                        this.params.updateGeneration(this.generationData, newStatementsCount);
                        this.gridApi.applyTransaction({ remove: [node.data] });
                        const index = this.params.data.children.findIndex((child) => child.id === id);
                        if (index !== -1) this.params.data.children.splice(index, 1);
                        this.checkAndRemoveGenerationIfEmpty();
                    }
                });
            },
            deleteStatementsFromGridRowData(statementIds) {
                const nodesToRemove = [];
                this.gridApi.forEachNode((node) => {
                    const statement = statementIds.find((id) => id === node.data.id);
                    if (statement) nodesToRemove.push(node.data);
                });
                if (nodesToRemove.length > 0) {
                    const newStatementsCount = this.generationData.statements_count - nodesToRemove.length;
                    this.params.updateGeneration(this.generationData, newStatementsCount);
                    this.gridApi.applyTransaction({ remove: nodesToRemove });
                    this.params.data.children = this.params.data.children.filter((s) => !statementIds.includes(s.id));
                    this.checkAndRemoveGenerationIfEmpty();
                }
            },
            handleSelectionChange() {
                const gridApi = this.$refs.grid.gridOptions.api;
                this.currentSelectedRows = gridApi.getSelectedRows();
            },
            async fetchOwnerStatement(id) {
                try {
                    const { data } = await API.get(OWNER_STATEMENT_ENDPOINT(id), {
                        params: { show_empty_statements: this.params.showEmptyStatements },
                    });
                    this.updateGridRowDataStatement(data);
                } catch (e) {
                    this.errorToast({ text: 'Could not load owner statement.' });
                }
            },
            openCommentsModal(existingComments, rowId) {
                this.newComments = existingComments || '';
                this.currentEditingRowId = rowId;
                this.$bvModal.show(this.commentsToOwnerContentModalId);
            },
            async submitComments() {
                const selectedNode = this.gridApi.getRowNode(this.currentEditingRowId);
                if (selectedNode) {
                    try {
                        selectedNode.setDataValue('comments', this.newComments);
                        await API.patch(OWNER_STATEMENT_ENDPOINT(selectedNode.data.id), {
                            comments: this.newComments,
                        });
                        this.successToast({ text: 'Comments updated successfully.' });
                        this.$bvModal.hide(this.commentsToOwnerContentModalId);
                    } catch (error) {
                        const { generic } = formatResponseError(error);
                        this.errorToast({ text: generic || 'Failed to update comments.' });
                    }
                }
            },
            getRowClass({ data }) {
                if (data.owner_name) {
                    return 'lx-owner-statement-row';
                }
                return '';
            },
        },
    };
</script>
<style lang="scss" scoped>
    @import '~bootstrap/scss/_functions.scss';
    @import '~bootstrap/scss/_variables.scss';
    @import '~bootstrap/scss/mixins/_breakpoints.scss';

    .statements-grid {
        padding: 0 2rem 1.5rem 2rem;

        @include media-breakpoint-down(md) {
            padding: 0;
        }
    }

    :deep(.statement-status-column) {
        .ag-header-cell-label {
            padding-left: 12px;
        }
    }
</style>
